import { Injectable } from '@angular/core';
import * as FAIwebviewer from '@occ/FAIwebviewer/wasm32/FAIwebviewer';
import {
  MouseGesture,
  MouseKey,
  ViewerInputBindings,
  ModifierKeys,
  MouseActionBinding,
  ScrollActions,
  SelectionManager,
} from '@occ/FAIwebviewer/wasm32/FAIwebviewer';
import { StateManagerProjectService } from '@state-manager/state-manager-project.service';
import { d3ViewToolsType, VIEWS_START_CONFIG } from './occ-viewer.data';
import { CASE_TYPE, BONE_MODEL_ORIENTATION } from '@viewer/cornerstone.data';

@Injectable()
export class OCCViewerService {
  public occViewer: FAIwebviewer.FAIwebviewer | null = null;
  public aViewsConfig: any = {};
  public viewerViews: any = {};
  public OCCViewerCanvas: HTMLElement | null = null;
  public isViewerLoading: boolean = false;
  public initialCameraViewpoint: FAIwebviewer.CameraViewPoint | null = null;
  public callbackAfterLoadModel: Function | undefined;

  private _viewerBindings: ViewerInputBindings | null = null;
  private _selectionManager: SelectionManager | null = null;

  constructor(
    private _stateManagerProjectService: StateManagerProjectService
  ) {}

  public init(): void {
    this.aViewsConfig = JSON.parse(JSON.stringify(VIEWS_START_CONFIG));
  }

  /**
   * Initializes custom viewer keyboard keys and mouse bindings
   */
  public initViewerBindings(): void {
    this._viewerBindings = this.occViewer?.viewerInputBindings!;
    this._selectionManager = this.occViewer?.selectionManager!;
    this._selectionManager.toAllowSelection = false;
    this._clearViewerBindings();
    this._initViewerMouseBindings();
    this._initViewerKeysBindings();
    this._initViewerScrollBindings();
  }

  public updateCanvasSize(viewer: any, canvas: HTMLElement | null): void {
    // size of canvas in logical (density-independent) units
    const viewerOCC = document.getElementById('viewer-OCC');
    const aSizeX = viewerOCC!.clientWidth;
    const aSizeY = viewerOCC!.clientHeight;

    canvas!.style.width = aSizeX - 1 + 'px';
    canvas!.style.height = aSizeY + 8 + 'px';

    viewer?.resizeViewport();
  }

  public resetView(): void {
    const ORIENTATION = this.getBoneModelOrientation();
    this.viewerViews.d3View.camera.setOrientation(
      FAIwebviewer.CameraOrientation[ORIENTATION]
    );
    this.occViewer!.viewManager.fitAllAuto(this.viewerViews.d3View.id, false);
    this.initialCameraViewpoint = this.viewerViews.d3View.camera.viewPoint;
    this.occViewer!.requestViewUpdate();
  }

  public destroy(): void {
    // remove last model
    this.initialCameraViewpoint = null;
    this.aViewsConfig.d3View.objects[0]?.remove();
    this.aViewsConfig.d3View.objects = [];
    this.aViewsConfig = {};
    this.callbackAfterLoadModel = undefined;
  }

  /**
   * Activates tool and assings behavior of the tool to LMB or deactivates the tool and assigns default behavior
   * @param toolToActivate tool which sould be toggled
   */
  public toggleTool(toolToActivate: d3ViewToolsType): void {
    const currentTool = this._stateManagerProjectService.current3dTool;
    if (!toolToActivate && !currentTool) {
      return;
    }

    this._clearViewerBindings();

    const bindingForLMB: MouseActionBinding = {
      keys: MouseKey.LeftButton,
      gesture: MouseGesture.None,
    };

    if (toolToActivate && toolToActivate === currentTool) {
      toolToActivate = null;
    }

    switch (toolToActivate) {
      case 'pan':
        bindingForLMB.gesture = MouseGesture.Pan;
        break;
      case 'rotation':
        bindingForLMB.gesture = MouseGesture.RotateOrbit;
        break;
      case 'zoom':
        bindingForLMB.gesture = MouseGesture.ZoomWindow;
        break;
      default:
        this.initViewerBindings();
    }

    if (toolToActivate) {
      this._viewerBindings?.setMouseBinding(bindingForLMB);
    } else {
      this._viewerBindings?.getMouseKeyBinding(MouseKey.LeftButton) &&
        this._viewerBindings.removeMouseKeyBinding(MouseKey.LeftButton);
    }

    this._stateManagerProjectService.current3dTool = toolToActivate;
  }

  /**
   * Initializes custom viewer mouse keys bindings
   */
  private _initViewerMouseBindings(): void {
    if (!this._viewerBindings) {
      return;
    }

    this._viewerBindings.setMouseBinding({
      keys: MouseKey.RightButton,
      gesture: MouseGesture.RotateOrbit,
    });
    this._viewerBindings.setMouseBinding({
      keys: MouseKey.MiddleButton,
      gesture: MouseGesture.Pan,
    });
  }

  /**
   * Initializes custom viewer mouse scroll bindings
   */
  private _initViewerScrollBindings(): void {
    if (!this._viewerBindings) {
      return;
    }

    this._viewerBindings.setScrollBinding({
      modifierKeys: ModifierKeys.CTRL,
      action: ScrollActions.Zoom,
      scrollMultiplier: 1,
    });
  }

  /**
   * Initializes custom viewer keyboard keys bindings
   */
  private _initViewerKeysBindings(): void {}

  /**
   * Removes all viewer mouse and keyboard keys bindings
   */
  private _clearViewerBindings(): void {
    if (!this._viewerBindings) {
      return;
    }
    for (const keyBinding of this._viewerBindings.keyBindings) {
      this._viewerBindings.removeKeyBinding(keyBinding.keys);
    }
    for (const mouseBinding of this._viewerBindings.mouseBindings) {
      this._viewerBindings.removeMouseKeyBinding(mouseBinding.keys);
    }
    for (const scrollBinding of this._viewerBindings.scrollBindings) {
      this._viewerBindings.removeScrollKeyBinding(scrollBinding.modifierKeys);
    }
  }

  getBoneModelOrientation(): string {
    return this._stateManagerProjectService.caseType === CASE_TYPE.FEMUR ? BONE_MODEL_ORIENTATION.FEMUR : BONE_MODEL_ORIENTATION.TIBIA;
  }
}
